// components/SubscriptionSuccessPage.js - Complete Version with Session Check Failsafe
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { 
  CheckCircle, 
  CreditCard, 
  Mail, 
  Clock, 
  ArrowRight,
  Shield,
  Zap,
  AlertCircle,
  Loader2,
  UserCheck,
  Link2,
  RefreshCw
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.svg';
import { SiteStyles } from '../site/SiteChrome';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const { api, fetchUserProfile } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  const [stripeConfig, setStripeConfig] = useState(null);
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  const [paymentLinked, setPaymentLinked] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  // Get session details from URL
  const sessionId = searchParams.get('session_id');
  const isNewUser = searchParams.get('new_user') === 'true';
  const userType = searchParams.get('user_type');
  const fromRegistration = searchParams.get('from_registration') === 'true';
  const subscriptionType = searchParams.get('type') || 'monthly';

  // Load Stripe config for fallback plan data
  useEffect(() => {
    const loadStripeConfig = async () => {
      try {
        const response = await fetch('/api/stripe/config');
        const config = await response.json();
        setStripeConfig(config);
      } catch (error) {
        console.error('Failed to load Stripe configuration:', error);
      }
    };
    loadStripeConfig();
  }, []);

  // Clear any stale error states when component mounts
  useEffect(() => {
    const staleError = sessionStorage.getItem('registrationError');
    if (staleError) {
      sessionStorage.removeItem('registrationError');
    }
  }, []);

  // Failsafe session check function
  const checkSessionPayment = async (sessionId, email) => {
    console.log('🔍 Running failsafe session check...');
    try {
      const response = await fetch('/api/subscription/check-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: email
        })
      });
      
      const result = await response.json();
      console.log('Session check result:', result);
      
      if (result.success && result.subscription_active) {
        console.log('✅ Session check successful - subscription activated');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session check error:', error);
      return false;
    }
  };

  // Enhanced data recovery function
  const recoverRegistrationData = () => {
    console.log('=== ATTEMPTING ENHANCED DATA RECOVERY ===');
    
    let userData = null;
    let planData = null;
    
    // 1. Check sessionStorage with all possible keys
    console.log('Step 1: Checking sessionStorage...');
    const sessionKeys = Object.keys(sessionStorage);
    console.log('Available sessionStorage keys:', sessionKeys);
    
    // Try pendingRegistration first (preferred)
    const pendingReg = sessionStorage.getItem('pendingRegistration');
    if (pendingReg) {
      try {
        const parsed = JSON.parse(pendingReg);
        if (parsed.userData) {
          userData = parsed.userData;
          planData = parsed.planData;
          console.log('✅ Found data in pendingRegistration');
        }
      } catch (e) {
        console.error('Failed to parse pendingRegistration:', e);
      }
    }
    
    // Try registrationData as fallback
    if (!userData) {
      const regData = sessionStorage.getItem('registrationData');
      if (regData) {
        try {
          userData = JSON.parse(regData);
          console.log('✅ Found data in registrationData');
        } catch (e) {
          console.error('Failed to parse registrationData:', e);
        }
      }
    }
    
    // 2. Check localStorage backup
    if (!userData) {
      console.log('Step 2: Checking localStorage backup...');
      const localBackup = localStorage.getItem('pendingRegistration_backup');
      if (localBackup) {
        try {
          const parsed = JSON.parse(localBackup);
          if (parsed.userData) {
            userData = parsed.userData;
            planData = parsed.planData;
            console.log('✅ Recovered data from localStorage backup');
            
            // Restore to sessionStorage
            sessionStorage.setItem('pendingRegistration', localBackup);
          }
        } catch (e) {
          console.error('Failed to parse localStorage backup:', e);
        }
      }
      
      // Also check registrationData backup
      if (!userData) {
        const regBackup = localStorage.getItem('registrationData_backup');
        if (regBackup) {
          try {
            userData = JSON.parse(regBackup);
            console.log('✅ Recovered userData from registrationData_backup');
          } catch (e) {
            console.error('Failed to parse registrationData_backup:', e);
          }
        }
      }
    }
    
    // 3. Check paymentAttempt for email recovery
    if (!userData) {
      console.log('Step 3: Checking payment attempt data...');
      const paymentAttempt = sessionStorage.getItem('paymentAttempt') || 
                            localStorage.getItem('paymentAttempt');
      if (paymentAttempt) {
        try {
          const attempt = JSON.parse(paymentAttempt);
          console.log('Found payment attempt:', attempt);
          
          // If we have an email, we might be able to recover
          if (attempt.email) {
            console.log('⚠️ Partial recovery from payment attempt - email found:', attempt.email);
            userData = { partialRecovery: true, email: attempt.email };
          }
        } catch (e) {
          console.error('Failed to parse payment attempt:', e);
        }
      }
    }
    
    // 4. Set default plan data if missing
    if (!planData) {
      planData = {
        id: 'monthly',
        name: 'Monthly Subscription',
        type: 'paid',
        stripePriceId: stripeConfig?.prices?.monthly
      };
      console.log('Using default plan data');
    }
    
    console.log('=== RECOVERY RESULT ===');
    console.log('User Data:', userData);
    console.log('Plan Data:', planData);
    
    return { userData, planData };
  };

  // Attempt to link payment to existing account
  const attemptPaymentLinkage = async (userData, sessionId) => {
    console.log('🔄 Attempting to link payment to existing account...');
    
    try {
      const response = await fetch('/api/auth/check-and-link-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          username: userData.username,
          stripe_session_id: sessionId
        })
      });
      
      const result = await response.json();
      console.log('Payment linkage result:', result);
      
      return result;
    } catch (error) {
      console.error('Failed to link payment:', error);
      return { error: error.message };
    }
  };

  // Handle existing user upgrade with better recovery
  const handleExistingUserUpgrade = async () => {
    console.log('Processing upgrade for existing user...');
    setLoading(true);
    setDebugInfo('Verifying payment and activating subscription...');
    
    try {
      // Wait for webhook to process
      console.log('Waiting for webhook processing...');
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Try verify payment status first
      let verified = false;
      
      if (api && sessionId) {
        try {
          const verifyResponse = await api.post('/api/stripe/verify-payment-status', {
            session_id: sessionId
          });
          
          console.log('Payment verification response:', verifyResponse.data);
          
          if (verifyResponse.data?.subscription_active || 
              verifyResponse.data?.payment_verified ||
              verifyResponse.data?.subscription_type === 'paid') {
            verified = true;
          }
        } catch (error) {
          console.error('Verification error:', error);
          // Continue to failsafe
        }
      }
      
      // If not verified, try failsafe session check
      if (!verified) {
        console.log('Primary verification failed, trying failsafe...');
        
        // Get email from various sources
        let email = userEmail;
        
        // Try to get from paymentAttempt
        if (!email) {
          const paymentAttempt = sessionStorage.getItem('paymentAttempt');
          if (paymentAttempt) {
            try {
              const attempt = JSON.parse(paymentAttempt);
              email = attempt.email;
            } catch (e) {}
          }
        }
        
        // Try failsafe check
        const sessionActivated = await checkSessionPayment(sessionId, email);
        
        if (sessionActivated) {
          verified = true;
          
          // Refresh user profile
          if (fetchUserProfile) {
            await fetchUserProfile(true);
          }
        }
      }
      
      if (verified) {
        console.log('✅ Payment verified successfully');
        setRegistrationCompleted(true);
        setError('');
        setDebugInfo('');
        setCountdown(3);
      } else {
        // Final retry with manual activation
        console.log('Attempting final manual activation...');
        
        if (api) {
          try {
            const manualResponse = await api.post('/api/subscription/manual-activate', {
              session_id: sessionId
            });
            
            if (manualResponse.data?.success) {
              console.log('✅ Manual activation successful');
              setRegistrationCompleted(true);
              setError('');
              setCountdown(3);
              
              if (fetchUserProfile) {
                await fetchUserProfile(true);
              }
            } else {
              throw new Error('Manual activation failed');
            }
          } catch (error) {
            console.error('Manual activation error:', error);
            setError('Your payment was successful but activation is delayed. Your subscription will be activated within a few minutes. Please refresh the page or contact support if the issue persists.');
            setDebugInfo('Payment confirmed - activation pending');
          }
        } else {
          setError('Your payment was successful. Please refresh the page to complete activation.');
        }
      }
      
    } catch (error) {
      console.error('Error processing existing user upgrade:', error);
      setError('Payment was successful but there was an issue. Please refresh the page or contact support.');
      setDebugInfo(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Main processing function
    const processPaymentSuccess = async () => {
      // Prevent multiple attempts
      if (registrationAttempted) {
        return;
      }
      setRegistrationAttempted(true);
  
      console.log('🔍 SubscriptionSuccess Starting...', {
        isNewUser,
        sessionId,
        subscriptionType,
        userType,
        fromRegistration
      });
      
      // Handle existing users (expired trial upgrading to paid)
      if (userType === 'existing') {
        await handleExistingUserUpgrade();
        return;
      }
      
      // Handle new user registration (user already created before payment)
      if (isNewUser || fromRegistration) {
        const { userData, planData } = recoverRegistrationData();
        
        if (userData && userData.email) {
          console.log('✅ Processing payment for user:', userData.email);
          console.log('💡 User was already registered before payment - no need to register again');
          
          try {
            // Check if payment linkage worked via webhook
            console.log('🔍 Checking if webhook already linked payment...');
            setDebugInfo('Verifying subscription activation...');
  
            const linkResult = await attemptPaymentLinkage(userData, sessionId);
            
            if (linkResult.exists) {
              setUserEmail(userData.email);
              
              if (linkResult.subscription_created || linkResult.has_subscription) {
                // Payment successfully linked by webhook
                console.log('✅ Payment linked to account by webhook');
                setError('');
                setDebugInfo('');
                setPaymentLinked(true);
                setRegistrationCompleted(true);
                
                // ✅ CHANGED: Don't clear storage yet - wait until redirect
                // Store success flag instead
                sessionStorage.setItem('paymentSuccess', 'true');
                sessionStorage.setItem('paymentSuccessEmail', userData.email);
                
                // Redirect to login
                setTimeout(() => {
                  // Clear session data just before redirect
                  const successEmail = sessionStorage.getItem('paymentSuccessEmail');
                  sessionStorage.clear();
                  localStorage.removeItem('pendingRegistration_backup');
                  localStorage.removeItem('registrationData_backup');
                  localStorage.removeItem('paymentAttempt');
                  
                  navigate('/login', {
                    state: {
                      message: 'Payment successful! Your subscription is active. Please log in.',
                      email: successEmail,
                      subscriptionActivated: true
                    },
                    replace: true // ✅ Use replace to prevent back button issues
                  });
                }, 3000);
                
                setLoading(false);
                return;
              } else {
                // User exists but webhook hasn't processed yet - try failsafe
                console.log('⏳ Webhook may still be processing - trying failsafe...');
                
                const sessionActivated = await checkSessionPayment(sessionId, userData.email);
                
                if (sessionActivated) {
                  console.log('✅ Failsafe activation successful');
                  setError('');
                  setPaymentLinked(true);
                  setRegistrationCompleted(true);
                  setUserEmail(userData.email);
                  
                  // Store success flag
                  sessionStorage.setItem('paymentSuccess', 'true');
                  sessionStorage.setItem('paymentSuccessEmail', userData.email);
                  
                  setTimeout(() => {
                    const successEmail = sessionStorage.getItem('paymentSuccessEmail');
                    sessionStorage.clear();
                    localStorage.removeItem('pendingRegistration_backup');
                    localStorage.removeItem('registrationData_backup');
                    localStorage.removeItem('paymentAttempt');
                    
                    navigate('/login', {
                      state: {
                        message: 'Payment successful! Your subscription is active. Please log in.',
                        email: successEmail
                      },
                      replace: true
                    });
                  }, 3000);
                } else {
                  // Webhook may be delayed - show success anyway
                  console.log('⏳ Webhook processing - showing success message');
                  setError('');
                  setRegistrationCompleted(true);
                  setUserEmail(userData.email);
                  setDebugInfo('Subscription is being activated...');
                  
                  // Store success flag
                  sessionStorage.setItem('paymentSuccess', 'true');
                  sessionStorage.setItem('paymentSuccessEmail', userData.email);
                  
                  setTimeout(() => {
                    const successEmail = sessionStorage.getItem('paymentSuccessEmail');
                    sessionStorage.clear();
                    localStorage.removeItem('pendingRegistration_backup');
                    localStorage.removeItem('registrationData_backup');
                    localStorage.removeItem('paymentAttempt');
                    
                    navigate('/login', {
                      state: {
                        message: 'Payment successful! Your subscription is being activated. Please log in.',
                        email: successEmail
                      },
                      replace: true
                    });
                  }, 3000);
                }
                
                setLoading(false);
                return;
              }
            } else {
              // User doesn't exist in linkage check (shouldn't happen, but handle gracefully)
              console.log('⚠️ User not found in linkage check - assuming webhook will process');
              setError('');
              setRegistrationCompleted(true);
              setUserEmail(userData.email);
              
              // Store success flag
              sessionStorage.setItem('paymentSuccess', 'true');
              sessionStorage.setItem('paymentSuccessEmail', userData.email);
              
              setTimeout(() => {
                const successEmail = sessionStorage.getItem('paymentSuccessEmail');
                sessionStorage.clear();
                localStorage.removeItem('pendingRegistration_backup');
                localStorage.removeItem('registrationData_backup');
                localStorage.removeItem('paymentAttempt');
                
                navigate('/login', {
                  state: {
                    message: 'Payment successful! Please log in to access your account.',
                    email: successEmail
                  },
                  replace: true
                });
              }, 3000);
              
              setLoading(false);
              return;
            }
            
          } catch (err) {
            console.error('❌ Payment processing error:', err);
            
            // Even on error, assume payment was successful (it was!)
            console.log('💡 Payment was successful - redirecting to login');
            setError('');
            setRegistrationCompleted(true);
            setUserEmail(userData.email);
            
            // Store success flag
            sessionStorage.setItem('paymentSuccess', 'true');
            sessionStorage.setItem('paymentSuccessEmail', userData.email);
            
            setTimeout(() => {
              const successEmail = sessionStorage.getItem('paymentSuccessEmail');
              sessionStorage.clear();
              localStorage.removeItem('pendingRegistration_backup');
              localStorage.removeItem('registrationData_backup');
              localStorage.removeItem('paymentAttempt');
              
              navigate('/login', {
                state: {
                  message: 'Payment successful! Please log in to access your account.',
                  email: successEmail
                },
                replace: true
              });
            }, 3000);
            
            setLoading(false);
            return;
          }
        }
        
        // ✅ CHANGED: Better fallback handling when no userData found
        console.error('⚠️ No registration data found after payment');
        
        // Check if we already processed this successfully
        if (sessionStorage.getItem('paymentSuccess') === 'true') {
          const successEmail = sessionStorage.getItem('paymentSuccessEmail');
          console.log('💡 Already processed - redirecting to login');
          
          if (successEmail) {
            setRegistrationCompleted(true);
            setUserEmail(successEmail);
            setError('');
            
            setTimeout(() => {
              sessionStorage.clear();
              localStorage.removeItem('pendingRegistration_backup');
              localStorage.removeItem('registrationData_backup');
              localStorage.removeItem('paymentAttempt');
              
              navigate('/login', {
                state: {
                  message: 'Payment successful! Please log in to access your account.',
                  email: successEmail
                },
                replace: true
              });
            }, 1000);
            
            setLoading(false);
            return;
          }
        }
        
        // Try to get email from payment attempt
        const paymentAttempt = sessionStorage.getItem('paymentAttempt');
        if (paymentAttempt) {
          try {
            const attempt = JSON.parse(paymentAttempt);
            if (attempt.email) {
              console.log('📧 Found email from payment attempt:', attempt.email);
              setUserEmail(attempt.email);
              setRegistrationCompleted(true);
              setError('');
              
              sessionStorage.setItem('paymentSuccess', 'true');
              sessionStorage.setItem('paymentSuccessEmail', attempt.email);
              
              setTimeout(() => {
                sessionStorage.clear();
                localStorage.removeItem('pendingRegistration_backup');
                localStorage.removeItem('registrationData_backup');
                localStorage.removeItem('paymentAttempt');
                
                navigate('/login', {
                  state: {
                    message: 'Payment successful! Please log in to access your account.',
                    email: attempt.email
                  },
                  replace: true
                });
              }, 3000);
              
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error('Failed to parse payment attempt:', e);
          }
        }
        
        // Try failsafe with session only
        console.log('⚠️ Attempting failsafe with session only');
        try {
          const sessionActivated = await checkSessionPayment(sessionId, null);
          
          if (sessionActivated) {
            console.log('✅ Failsafe succeeded');
            setError('');
            setRegistrationCompleted(true);
            
            setTimeout(() => {
              sessionStorage.clear();
              localStorage.removeItem('pendingRegistration_backup');
              localStorage.removeItem('registrationData_backup');
              localStorage.removeItem('paymentAttempt');
              
              navigate('/login', {
                state: {
                  message: 'Payment successful! Please log in to access your account.'
                },
                replace: true
              });
            }, 3000);
            
            setLoading(false);
            return;
          }
        } catch (failsafeErr) {
          console.error('Failsafe error:', failsafeErr);
        }
        
        // Final fallback: Show error with support contact
        setError('Payment was successful! However, we need to manually verify your account. Please contact support with your payment confirmation.');
        setDebugInfo(`Session: ${sessionId}`);
        
        // Store for support
        localStorage.setItem('paymentConfirmation', JSON.stringify({
          sessionId,
          issue: 'no_registration_data',
          timestamp: new Date().toISOString()
        }));
        
        setLoading(false);
        return;
      }
      
      // Generic success handling
      console.log('✅ Processing subscription success');
      setDebugInfo('Subscription activated');
      setRegistrationCompleted(true);
      setLoading(false);
    };
    
    // Run when component mounts
    processPaymentSuccess();
  }, []); // Empty dependency array - run once on mount
  
  // Countdown and redirect
  useEffect(() => {
    if (!loading && !error && registrationCompleted) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            
            // Redirect based on user type
            if (userType === 'existing') {
              navigate('/stockwatch', { replace: true });
            } else if (paymentLinked) {
              navigate('/login');
            } else if (isNewUser) {
              navigate('/pending-approval');
            } else {
              navigate('/stockwatch');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [loading, error, registrationCompleted, isNewUser, paymentLinked, userType, navigate]);

  // Manual retry function
  const handleManualRetry = async () => {
    setRetryCount(prev => prev + 1);
    setError('');
    setLoading(true);
    
    try {
      // Get email from storage
      let email = userEmail;
      if (!email) {
        const paymentAttempt = sessionStorage.getItem('paymentAttempt');
        if (paymentAttempt) {
          try {
            const attempt = JSON.parse(paymentAttempt);
            email = attempt.email;
          } catch (e) {}
        }
      }
      
      // Try failsafe activation
      const sessionActivated = await checkSessionPayment(sessionId, email);
      
      if (sessionActivated) {
        setError('');
        setRegistrationCompleted(true);
        
        // Refresh user if possible
        if (fetchUserProfile) {
          await fetchUserProfile(true);
        }
        
        setTimeout(() => {
          navigate('/stockwatch', { replace: true });
        }, 2000);
      } else {
        setError('Unable to activate subscription. Please contact support with your payment confirmation.');
      }
    } catch (error) {
      console.error('Manual retry error:', error);
      setError('Retry failed. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Debug function
  const checkAllStorage = () => {
    const data = {
      sessionStorage: {},
      localStorage: {}
    };
    
    // Check sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      try {
        data.sessionStorage[key] = JSON.parse(sessionStorage.getItem(key));
      } catch {
        data.sessionStorage[key] = sessionStorage.getItem(key);
      }
    });
    
    // Check relevant localStorage
    ['pendingRegistration_backup', 'registrationData_backup', 'paymentAttempt', 'paymentConfirmation'].forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          data.localStorage[key] = JSON.parse(value);
        } catch {
          data.localStorage[key] = value;
        }
      }
    });
    
    console.log('=== ALL STORAGE DATA ===', data);
    setDebugInfo(JSON.stringify(data, null, 2));
  };

  // Retry registration
  const retryRegistration = () => {
    sessionStorage.setItem('retryingRegistration', 'true');
    sessionStorage.setItem('stripe_session_id', sessionId);
    navigate('/register');
  };

  // Go to login
  const goToLogin = () => {
    const paymentConf = localStorage.getItem('paymentConfirmation');
    let email = userEmail;
    
    if (!email && paymentConf) {
      try {
        const conf = JSON.parse(paymentConf);
        email = conf.email;
      } catch {}
    }
    
    sessionStorage.clear();
    navigate('/login', { 
      state: { 
        message: 'Your payment was successful. Please login with your account.',
        email: email
      }
    });
  };

  // Contact support
  const contactSupport = () => {
    const supportData = {
      sessionId,
      timestamp: new Date().toISOString(),
      issue: error,
      email: userEmail
    };
    
    const paymentConf = localStorage.getItem('paymentConfirmation');
    if (paymentConf) {
      try {
        supportData.paymentConfirmation = JSON.parse(paymentConf);
      } catch {}
    }
    
    console.log('Support data:', supportData);
    const emailBody = encodeURIComponent(`
Session ID: ${sessionId}
Time: ${supportData.timestamp}
Email: ${userEmail || 'Unknown'}
Issue: ${error}

My payment was successful but I'm having trouble accessing my account.
    `);
    
    window.location.href = `mailto:support@tunasignal.ai?subject=Payment%20Issue&body=${emailBody}`;
  };

  if (loading) {
    return (
      <div className="dark ts-page">
      <SiteStyles />
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
      }`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600 mb-4" />
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
            {userType === 'existing' ? 'Upgrading your subscription...' : 'Processing your subscription...'}
          </p>
          {debugInfo && (
            <p className="text-sm text-blue-600 mt-2">{debugInfo}</p>
          )}
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className={`w-full max-w-2xl ${
          darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
        } rounded-md overflow-hidden`}>
          
          {/* Success Header */}
          <div className="relative bg-[#0D1E2E] border-b border-[#1C3348] p-8 text-white">
            
            <div className="relative">
              <CheckCircle className="w-7 h-7 mb-3 text-emerald-400" aria-hidden="true" />
              <h1 className="text-2xl font-semibold mb-1">Payment Successful!</h1>
              <p className="text-slate-400">
                {userType === 'existing' ? 'Your subscription has been upgraded' : 'Your subscription has been activated'}
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8">
            {error ? (
              <div className={`p-4 rounded-lg border ${
                darkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-300 text-red-700'
              }`}>
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">Processing Issue</p>
                    <p className="text-sm mt-1">{error}</p>
                    {userEmail && (
                      <p className="text-sm mt-2 opacity-75">
                        Account email: {userEmail}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {retryCount < 3 && (
                    <button
                      onClick={handleManualRetry}
                      className="text-sm bg-[#5AA9F0] text-[#07131F] px-3 py-1 rounded hover:bg-[#8CC4F5] transition-colors flex items-center"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Retry Activation
                    </button>
                  )}
                  
                  <button
                    onClick={checkAllStorage}
                    className="text-sm bg-[#1C3348] text-white px-3 py-1 rounded hover:bg-[#13283C] transition-colors"
                  >
                    Debug Session Data
                  </button>
                  
                  {!error.includes('already exists') && isNewUser && (
                    <button
                      onClick={retryRegistration}
                      className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition-colors"
                    >
                      Retry Registration
                    </button>
                  )}
                  
                  <button
                    onClick={goToLogin}
                    className="text-sm bg-[#5AA9F0] text-[#07131F] px-3 py-1 rounded hover:bg-[#8CC4F5] transition-colors"
                  >
                    Go to Login
                  </button>
                  
                  <button
                    onClick={contactSupport}
                    className="text-sm bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700 transition-colors"
                  >
                    Contact Support
                  </button>
                </div>
                
                {debugInfo && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Debug Information:</p>
                    <pre className="p-2 bg-[#07131F] text-green-400 text-xs rounded overflow-auto max-h-40">
                      {debugInfo}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-8">
                  {userType === 'existing' ? (
                    // Existing user upgrade success
                    <>
                      <div className="flex items-start">
                        <Zap className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Subscription Upgraded Successfully
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Your monthly subscription is now active
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Full Premium Access
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            All premium features are now unlocked
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Mail className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            A confirmation email has been sent to your registered address
                          </p>
                        </div>
                      </div>
                    </>
                  ) : paymentLinked ? (
                    // Payment linked to existing account
                    <>
                      <div className="flex items-start">
                        <Link2 className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Payment Successfully Linked
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Your subscription has been activated for {userEmail}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <UserCheck className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Account Ready
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Please login to access your account
                          </p>
                        </div>
                      </div>
                    </>
                  ) : isNewUser ? (
                    // New user registration complete
                    <>
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Registration Complete
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Your account has been created and approved
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Full Access Active
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            You now have full access to all TunaSignal AI features
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Mail className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                        <div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            A confirmation email has been sent to your registered email
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Generic success
                    <>
                      <div className="flex items-start">
                        <Zap className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <div>
                          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            Your subscription is now active
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                        <div>
                          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            You have full access to all premium features
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Mail className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                        <div>
                          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            A confirmation email has been sent
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Auto-redirect Notice */}
                <div className={`text-center p-4 rounded-lg ${
                  darkMode ? 'bg-[#13283C]' : 'bg-blue-50'
                }`}>
                  <div className="flex items-center justify-center mb-2">
                    <Clock className="w-5 h-5 text-blue-600 mr-2" />
                    <p className={`text-sm font-medium ${
                      darkMode ? 'text-blue-300' : 'text-blue-700'
                    }`}>
                      Redirecting in {countdown} seconds...
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(
                      userType === 'existing' ? '/stockwatch' :
                      paymentLinked ? '/login' : 
                      isNewUser ? '/pending-approval' : 
                      '/stockwatch'
                    )}
                    className={`inline-flex items-center text-sm font-medium transition-colors ${
                      darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    {userType === 'existing' ? 'Go to Dashboard' :
                     paymentLinked ? 'Go to Login' :
                     isNewUser ? 'Go to Pending Approval' : 
                     'Go to Dashboard'}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className={`px-8 py-4 border-t ${
            darkMode ? 'bg-[#07131F] border-[#1C3348]' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <img src={tunaLogo} alt="TunaSignal AI" className="h-6" />
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Need help? Contact support@tunasignal.ai
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SubscriptionSuccessPage;